import { stripTokenLoginCredentialsFromHref } from "./utils/auth/tokenLoginCredentialUrl";

const BOOTSTRAP_RECOVERY_GUARD = "hc.bootstrap.recovery-attempted.v1";

interface BootstrapRecoveryDependencies {
  currentHref?: string;
  reload?: () => void;
  replaceHistory?: (safePath: string) => void;
  replaceLocation?: (safePath: string) => void;
  storage?: Pick<Storage, "getItem" | "setItem">;
}

type CredentialScrubResult = "ready" | "navigating" | "failed";

const scrubBootstrapCredentialParams = (
  dependencies: BootstrapRecoveryDependencies,
): CredentialScrubResult => {
  const safePath = stripTokenLoginCredentialsFromHref(
    dependencies.currentHref ?? window.location.href,
  );
  if (!safePath) return "ready";

  try {
    const replaceHistory =
      dependencies.replaceHistory ??
      ((path: string) =>
        window.history.replaceState(
          window.history.state,
          document.title,
          path,
        ));
    replaceHistory(safePath);
    return "ready";
  } catch {
    try {
      const replaceLocation =
        dependencies.replaceLocation ??
        ((path: string) => window.location.replace(path));
      replaceLocation(safePath);
      return "navigating";
    } catch {
      return "failed";
    }
  }
};

/** Returns true when a credential-safe automatic recovery navigation starts. */
export const attemptBootstrapRecovery = (
  dependencies: BootstrapRecoveryDependencies = {},
): boolean => {
  const credentialScrub = scrubBootstrapCredentialParams(dependencies);
  if (credentialScrub === "navigating") return true;
  if (credentialScrub === "failed") return false;

  try {
    const storage = dependencies.storage ?? window.sessionStorage;
    if (storage.getItem(BOOTSTRAP_RECOVERY_GUARD) !== null) return false;
    storage.setItem(BOOTSTRAP_RECOVERY_GUARD, "attempted");
  } catch {
    return false;
  }

  try {
    (dependencies.reload ?? (() => window.location.reload()))();
    return true;
  } catch {
    return false;
  }
};

/** Runs a credential-safe user-requested retry without the automatic-loop guard. */
export const attemptManualBootstrapRecovery = (
  dependencies: Omit<BootstrapRecoveryDependencies, "storage"> = {},
): boolean => {
  const credentialScrub = scrubBootstrapCredentialParams(dependencies);
  if (credentialScrub === "navigating") return true;
  if (credentialScrub === "failed") return false;

  try {
    (dependencies.reload ?? (() => window.location.reload()))();
    return true;
  } catch {
    return false;
  }
};

export const clearBootstrapRecoveryGuard = (
  storage?: Pick<Storage, "removeItem">,
): void => {
  try {
    (storage ?? window.sessionStorage).removeItem(BOOTSTRAP_RECOVERY_GUARD);
  } catch {
    // Storage can be unavailable in restricted browser modes. A manual reload
    // remains available on the dependency-free recovery screen.
  }
};

export const createBootstrapCorrelationId = (): string => {
  try {
    if (typeof globalThis.crypto.randomUUID === "function") {
      return `bootstrap-${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // Fall through to a support-safe, non-secret local identifier.
  }
  return `bootstrap-${Date.now().toString(36)}`;
};
